import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Download, Loader2, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import Seo, { SITE_URL } from "@/components/Seo";
import { api, aud } from "@/lib/api";
import { IMAGES, ARTICLES, PRODUCT_RELATED } from "@/data/site";
import { AUDIENCE_PAGES } from "@/data/audiences";

const ProductPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [all, setAll] = useState([]);
  const [missing, setMissing] = useState(false);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    setProduct(null);
    setMissing(false);
    api
      .get("/products")
      .then(({ data }) => {
        setAll(data);
        const p = data.find((x) => x.id === id);
        if (p) setProduct(p);
        else setMissing(true);
      })
      .catch(() => setMissing(true));
  }, [id]);

  const buy = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: [product.id],
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Checkout could not be started. Please try again.");
      setBuying(false);
    }
  };

  if (missing) {
    return (
      <div className="py-32 text-center" data-testid="product-not-found">
        <h1 className="font-serif text-3xl text-teal">Toolkit not found</h1>
        <Link to="/toolkits" className="mt-4 inline-block text-sm text-teal underline underline-offset-4">
          Browse all toolkits
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="py-32 flex justify-center">
        <Loader2 className="w-8 h-8 text-teal animate-spin" />
      </div>
    );
  }

  const related = PRODUCT_RELATED[product.id] || { audience: null, articles: [] };
  const audience = related.audience === "rural" ? null : AUDIENCE_PAGES[related.audience];
  const relatedArticles = ARTICLES.filter((a) => related.articles.includes(a.slug));
  const others = all.filter((p) => p.id !== product.id).slice(0, 3);

  const jsonLd = product.available
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.title,
        description: product.description,
        image: product.image?.startsWith("/") ? `${SITE_URL}${product.image}` : product.image,
        brand: { "@type": "Organization", name: "PLAN FIRST NSW" },
        offers: {
          "@type": "Offer",
          url: `${SITE_URL}/toolkits/${product.id}`,
          priceCurrency: "AUD",
          price: product.price.toFixed(2),
          availability: "https://schema.org/InStock",
        },
      }
    : null;

  return (
    <div data-testid="product-page">
      <Seo
        title={product.title}
        description={product.description.length > 155 ? product.description.slice(0, 152) + "…" : product.description}
        canonicalPath={`/toolkits/${product.id}`}
      />
      {jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      )}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Toolkits", item: `${SITE_URL}/toolkits` },
              { "@type": "ListItem", position: 3, name: product.title, item: `${SITE_URL}/toolkits/${product.id}` },
            ],
          }),
        }}
      />

      <section className="bg-sand-light/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <Link to="/toolkits" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-charcoal/60 hover:text-teal transition-colors">
            <ArrowLeft className="w-4 h-4" /> All toolkits
          </Link>
          <div className="mt-8 grid lg:grid-cols-2 gap-10 items-center">
            <FadeIn>
              <span className="inline-block bg-sand-light text-charcoal/60 text-[10px] font-mono uppercase tracking-[0.16em] px-3 py-1">
                {product.tag}
              </span>
              <h1 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight text-teal text-balance">
                {product.title}
              </h1>
              <p className="mt-4 text-charcoal/75 leading-relaxed">{product.description}</p>
              <div className="mt-7 flex flex-wrap items-center gap-4">
                <span className="font-serif text-3xl text-teal" data-testid="product-price">{aud(product.price)}</span>
                <span className="text-xs text-charcoal/55 font-mono uppercase tracking-[0.12em]">
                  GST included · Instant PDF
                </span>
              </div>
              <div className="mt-6">
                {product.available ? (
                  <button
                    onClick={buy}
                    disabled={buying}
                    data-testid="product-buy"
                    className="inline-flex items-center gap-2 bg-teal text-ivory text-sm font-medium px-7 py-3.5 rounded-full hover:bg-teal-dark transition-colors disabled:opacity-60"
                  >
                    {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Buy &amp; download now
                  </button>
                ) : (
                  <span
                    className="inline-flex items-center gap-2 border border-cloud text-charcoal/50 text-sm font-medium px-7 py-3.5 rounded-full"
                    data-testid="product-coming-soon"
                  >
                    <Clock className="w-4 h-4" /> Coming soon — in final preparation
                  </span>
                )}
              </div>
              {product.available && product.parts > 1 && (
                <p className="mt-3 text-xs text-charcoal/55">{product.parts}-part package — all PDFs included.</p>
              )}
            </FadeIn>
            <FadeIn delay={0.1}>
              <img
                src={IMAGES[product.image] || product.image}
                alt={product.title}
                className="w-full h-72 lg:h-96 object-cover shadow-lg"
                data-testid="product-image"
              />
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-18">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
          <FadeIn>
            <Eyebrow>Who it's for</Eyebrow>
            <p className="mt-4 text-charcoal/75 leading-relaxed" data-testid="product-audience">{product.audience}</p>
          </FadeIn>
          <FadeIn delay={0.08}>
            <Eyebrow>What's inside</Eyebrow>
            <ul className="mt-4 space-y-2.5" data-testid="product-includes">
              {product.includes.map((item, i) => (
                <li key={i} className="flex gap-3 text-sm text-charcoal/80 leading-relaxed">
                  <CheckCircle2 className="w-4 h-4 text-teal shrink-0 mt-0.5" />{item}
                </li>
              ))}
            </ul>
          </FadeIn>
        </div>
      </section>

      <section className="pb-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="bg-sand-light/60 border border-cloud p-6" data-testid="product-workbook-notice">
              <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-teal">About this resource</p>
              <p className="mt-2 text-xs text-charcoal/70 leading-relaxed max-w-3xl">
                An educational preparation resource — it helps you organise information, identify planning
                considerations and prepare questions for the appropriate authorities or professional advisers.
                It isn't a professional assessment, formal due diligence report or compliance certificate, and
                completing it doesn't establish that a development is permissible, compliant or approved.{" "}
                <Link to="/legal/disclaimer" className="text-teal underline underline-offset-2">Learn more</Link>
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {(audience || related.audience === "rural" || relatedArticles.length > 0) && (
        <section className="pb-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <Eyebrow>Keep exploring</Eyebrow>
              <div className="mt-6 grid sm:grid-cols-3 gap-4">
                {audience && (
                  <Link
                    to={`/start/${related.audience}`}
                    data-testid="product-related-audience"
                    className="group bg-teal text-ivory p-6 hover:bg-teal-dark transition-colors"
                  >
                    <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-sand">Your pathway</p>
                    <p className="mt-2 font-serif text-lg leading-snug">{audience.title}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.14em] text-ivory/70">
                      Explore <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </Link>
                )}
                {related.audience === "rural" && (
                  <Link
                    to="/rural"
                    data-testid="product-related-audience"
                    className="group bg-teal text-ivory p-6 hover:bg-teal-dark transition-colors"
                  >
                    <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-sand">Your pathway</p>
                    <p className="mt-2 font-serif text-lg leading-snug">Rural Land — Unlock Your Land's Potential</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.14em] text-ivory/70">
                      Explore <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </Link>
                )}
                {relatedArticles.map((a) => (
                  <Link
                    key={a.slug}
                    to={`/hub/${a.slug}`}
                    data-testid={`product-related-article-${a.slug}`}
                    className="group bg-white border border-cloud p-6 hover:border-teal transition-colors"
                  >
                    <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-charcoal/50">From the Planning Hub</p>
                    <p className="mt-2 font-serif text-lg text-teal leading-snug">{a.title}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.14em] text-teal/70">
                      Read the guide <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  </Link>
                ))}
              </div>
            </FadeIn>
          </div>
        </section>
      )}

      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <Eyebrow>You might also explore</Eyebrow>
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              {others.map((p) => (
                <Link
                  key={p.id}
                  to={`/toolkits/${p.id}`}
                  data-testid={`product-other-${p.id}`}
                  className="group bg-white border border-cloud p-6 hover:border-teal transition-colors"
                >
                  <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-charcoal/50">{p.tag}</p>
                  <p className="mt-2 font-serif text-base text-teal leading-snug">{p.title}</p>
                  <p className="mt-2 text-xs text-charcoal/60">
                    {p.available ? `${aud(p.price)} · GST incl.` : "Coming soon"}
                  </p>
                </Link>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default ProductPage;
